import * as React from "react";
import { Building2 } from "lucide-react";
import { ListViewCard } from "../ListViewCard";
import { formatRoleLabel, shortDate } from "../DeckCard/logic/formatters";
import * as s from "../ListViewCard/ListViewCard.style.css";
import type {
  ListViewCardAction,
  ListViewCardBadge,
  ListViewCardLayout,
  ListViewCardVariant,
  ListViewCTAGroup,
} from "../ListViewCard";

export interface ListViewOrganizationCardOrg {
  id: string;
  name: string;
  logoUrl?: string | null;
  description?: string | null;
  city?: string | null;
  state?: string | null;
  deletedAt?: string | null;
  createdAt?: string;
  isPreApproved?: boolean;
  isMember?: boolean;
  isPendingRequest?: boolean;
  isInvited?: boolean;
  /** User's role in this organization (prefer label over name for display). */
  roleLabel?: string | null;
  roleName?: string | null;
  roleColor?: string | null;
}

export interface ListViewOrganizationCardProps {
  layout: ListViewCardLayout;
  organization: ListViewOrganizationCardOrg;
  contextActions?: ListViewCardAction[];
  actionsDisabled?: boolean;
  onClick?: () => void;
  ctaGroups?: ListViewCTAGroup[];
  animatedHighlight?: boolean;
}

function displayRoleLabel(org: ListViewOrganizationCardOrg): string | null {
  const label = org.roleLabel?.trim();
  if (label) return label;
  if (org.roleName?.trim()) return formatRoleLabel(org.roleName);
  return null;
}

export function ListViewOrganizationCard({
  layout,
  organization: org,
  contextActions,
  actionsDisabled,
  onClick,
  ctaGroups,
  animatedHighlight,
}: ListViewOrganizationCardProps) {
  const image = (
    <div className={layout === "deck" ? s.logoBoxDeck : s.logoBox}>
      {org.logoUrl ? (
        <img
          src={org.logoUrl}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Building2
          size={layout === "deck" ? 40 : 32}
          style={{ opacity: 0.55 }}
        />
      )}
    </div>
  );

  const roleDisplay = displayRoleLabel(org);

  const statusBadge: ListViewCardBadge = org.deletedAt
    ? { id: "status", label: "Disabled", variant: "disabled" }
    : org.isPendingRequest
      ? { id: "status", label: "Pending", variant: "pending" }
      : org.isInvited
        ? { id: "status", label: "Invited", variant: "invited" }
        : org.isPreApproved && !org.isMember
          ? { id: "status", label: "Available", variant: "default" }
          : roleDisplay
            ? { id: "status", label: "Active", variant: "active" }
            : { id: "status", label: "Member", variant: "active" };

  const roleBadge: ListViewCardBadge | null = roleDisplay
    ? {
        id: "role",
        label: roleDisplay,
        color: org.roleColor ?? undefined,
      }
    : null;

  const locationStr = [org.city, org.state].filter(Boolean).join(", ");
  const subtitle = locationStr || org.description || undefined;
  const footerText = org.createdAt
    ? `Created ${shortDate(org.createdAt)}`
    : undefined;

  const variant: ListViewCardVariant =
    org.isPreApproved && !org.isMember && !org.isPendingRequest
      ? "attention"
      : "default";

  const badges = [statusBadge, ...(roleBadge ? [roleBadge] : [])];

  return (
    <ListViewCard
      id={org.id}
      layout={layout}
      imageFormat="logo"
      image={image}
      title={org.name}
      subtitle={subtitle}
      badges={badges}
      footerText={footerText}
      onClick={onClick}
      contextActions={contextActions}
      actionsDisabled={actionsDisabled}
      variant={variant}
      ctaGroups={ctaGroups}
      animatedHighlight={animatedHighlight}
    />
  );
}
